import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import { toast } from "sonner";
import { FilePdf, Copy, ShareNetwork } from "@phosphor-icons/react";import api, { formatApiError } from "../api";
import { copyToClipboard } from "../lib/clipboard";
import { BrutalButton } from "./Brutal";
import { saveCardPng } from "./CodeCardDialog";

const GOLD = "#c9a24a";
const GOLD_DARK = "#8a6a1c";
const CAT_ORDER = ["Injectable", "Precision Nasal Mist", "Topical", "Flagship Topical", "supplies",
  "Mushroom Coffee", "Adaptogens", "Protein", "Supplements", "Other"];

const priceText = (p, askLabel) => {
  if (p.price == null) return askLabel;
  const n = Number(p.price);
  return `$${n % 1 === 0 ? n : n.toFixed(2)}`;
};

/** First ~90 chars of the store description, cut at a word boundary. */
const brief = (desc) => {
  const d = (desc || "").trim();
  if (!d) return "";
  if (d.length <= 90) return d;
  const cut = d.slice(0, 90);
  return `${cut.slice(0, cut.lastIndexOf(" ") > 40 ? cut.lastIndexOf(" ") : 90)}…`;
};

const groupProducts = (products) => {
  const buckets = {};
  for (const p of products || []) {
    if (p.coming_soon) continue; // v414.62 — unreleased cards never print on price lists
    const raw = (p.category || "").trim();
    const cat = CAT_ORDER.find((c) => c.toLowerCase() === raw.toLowerCase()) || (raw ? "Other" : "Injectable");
    (buckets[cat] = buckets[cat] || []).push(p);
  }
  return CAT_ORDER
    .filter((c) => buckets[c]?.length)
    .map((c) => [c, buckets[c].sort((a, b) => a.name.localeCompare(b.name))]);
};

/** Canvas price-list image. Two passes: measure exact height, then draw. */
function composePriceListPng(qrCanvas, { storeName, tagline, groups, url, currency, t }) {
  const W = 900, PAD = 70, SCALE = 2;
  const askLabel = t("price_list.ask");

  const layout = (ctx, draw) => {
    let y = 108;
    const center = (txt, font, color) => {
      if (draw) { ctx.fillStyle = color; ctx.font = font; ctx.textAlign = "center"; ctx.fillText(txt, W / 2, y); }
    };
    center(t("price_list.heading").toUpperCase(), "600 28px ui-monospace, monospace", GOLD_DARK);
    y += 62;
    center(storeName, "700 46px ui-sans-serif, system-ui", "#09090b");
    const tg = (tagline || "").trim();
    if (tg) { y += 46; center(tg, "italic 600 28px ui-sans-serif, system-ui", GOLD_DARK); }
    y += 40;
    center(t("price_list.prices_in", { currency }), "400 22px ui-sans-serif, system-ui", "#71717a");
    y += 34;
    if (draw) { ctx.strokeStyle = GOLD; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y); ctx.stroke(); }

    ctx.textAlign = "left";
    for (const [cat, items] of groups) {
      y += 58;
      if (draw) {
        ctx.fillStyle = GOLD_DARK; ctx.font = "700 24px ui-monospace, monospace"; ctx.textAlign = "left";
        ctx.fillText(t(`categories.${cat}`, cat).toUpperCase(), PAD, y);
      }
      y += 8;
      for (const p of items) {
        y += 44;
        if (draw) {
          ctx.fillStyle = "#09090b"; ctx.font = "600 29px ui-sans-serif, system-ui"; ctx.textAlign = "left";
          let name = p.name;
          while (ctx.measureText(name).width > 600 && name.length > 4) name = `${name.slice(0, -2)}…`;
          ctx.fillText(name, PAD, y);
          ctx.textAlign = "right"; ctx.font = "700 29px ui-sans-serif, system-ui";
          ctx.fillText(priceText(p, askLabel), W - PAD, y);
          if (p.compare_at && p.price != null) {
            ctx.fillStyle = "#a1a1aa"; ctx.font = "400 20px ui-sans-serif, system-ui";
            ctx.fillText(t("price_list.was", { price: `$${p.compare_at}` }), W - PAD, y + 24);
          }
        }
        const d = brief(p.description);
        if (d) {
          if (draw) { ctx.fillStyle = "#71717a"; ctx.font = "400 21px ui-sans-serif, system-ui"; ctx.textAlign = "left"; }
          const words = d.split(" ");
          let line = "", lines = 0;
          for (const w of words) {
            const test = line ? `${line} ${w}` : w;
            if (draw && ctx.measureText(test).width > 620 && lines < 1) {
              y += 28; ctx.fillText(line, PAD, y); line = w; lines += 1;
            } else if (!draw && test.length > 58 && lines < 1) {
              y += 28; line = w; lines += 1;
            } else line = test;
          }
          if (line) { y += 28; if (draw) ctx.fillText(line, PAD, y); }
        }
        y += 10;
      }
    }

    y += 48;
    if (draw) { ctx.strokeStyle = GOLD; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(PAD, y); ctx.lineTo(W - PAD, y); ctx.stroke(); }
    y += 40;
    if (draw && qrCanvas) ctx.drawImage(qrCanvas, (W - 340) / 2, y, 340, 340);
    y += 340 + 54;
    center(url.replace(/^https?:\/\//, ""), "600 28px ui-monospace, monospace", GOLD_DARK);
    y += 44;
    center(t("price_list.footer"), "400 24px ui-sans-serif, system-ui", "#52525b");
    return y + 70;
  };

  // measure
  const probe = document.createElement("canvas").getContext("2d");
  const H = layout(probe, false);
  // draw
  const c = document.createElement("canvas");
  c.width = W * SCALE; c.height = H * SCALE;
  const ctx = c.getContext("2d");
  ctx.scale(SCALE, SCALE);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, W, H);
  ctx.strokeStyle = GOLD; ctx.lineWidth = 8; ctx.strokeRect(14, 14, W - 28, H - 28);
  layout(ctx, true);
  return c.toDataURL("image/png");
}

/** Clean multi-page A4 PDF via jsPDF. */
function buildPdf(qrCanvas, { storeName, tagline, groups, url, currency, code, t }) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const Wp = 595, M = 52, R = Wp - M;
  const askLabel = t("price_list.ask");
  let y = 64;
  const breakIf = (need) => { if (y + need > 790) { doc.addPage(); y = 56; } };

  doc.setTextColor(GOLD_DARK); doc.setFont("courier", "bold"); doc.setFontSize(11);
  doc.text(t("price_list.heading").toUpperCase(), Wp / 2, y, { align: "center" });
  y += 26;
  doc.setTextColor("#09090b"); doc.setFont("helvetica", "bold"); doc.setFontSize(20);
  doc.text(storeName, Wp / 2, y, { align: "center" });
  const tg = (tagline || "").trim();
  if (tg) {
    y += 20; doc.setTextColor(GOLD_DARK); doc.setFont("helvetica", "bolditalic"); doc.setFontSize(11);
    doc.text(tg, Wp / 2, y, { align: "center" });
  }
  y += 16; doc.setTextColor("#71717a"); doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(t("price_list.prices_in", { currency }), Wp / 2, y, { align: "center" });
  y += 14; doc.setDrawColor(GOLD); doc.setLineWidth(1.5); doc.line(M, y, R, y);

  for (const [cat, items] of groups) {
    breakIf(56);
    y += 30;
    doc.setTextColor(GOLD_DARK); doc.setFont("courier", "bold"); doc.setFontSize(10);
    doc.text(t(`categories.${cat}`, cat).toUpperCase(), M, y);
    y += 4;
    for (const p of items) {
      const d = brief(p.description);
      const dLines = d ? doc.splitTextToSize(d, 410) : [];
      breakIf(22 + dLines.length * 11 + 6);
      y += 18;
      doc.setTextColor("#09090b"); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
      doc.text(doc.splitTextToSize(p.name, 390)[0], M, y);
      doc.text(priceText(p, askLabel), R, y, { align: "right" });
      if (p.compare_at && p.price != null) {
        doc.setTextColor("#a1a1aa"); doc.setFont("helvetica", "normal"); doc.setFontSize(8);
        doc.text(t("price_list.was", { price: `$${p.compare_at}` }), R, y + 10, { align: "right" });
      }
      if (dLines.length) {
        doc.setTextColor("#71717a"); doc.setFont("helvetica", "normal"); doc.setFontSize(8.5);
        for (const ln of dLines.slice(0, 2)) { y += 11; doc.text(ln, M, y); }
      }
      y += 6;
    }
  }

  breakIf(250);
  y += 26; doc.setDrawColor(GOLD); doc.line(M, y, R, y);
  y += 20;
  if (qrCanvas) doc.addImage(qrCanvas.toDataURL("image/png"), "PNG", (Wp - 130) / 2, y, 130, 130);
  y += 148;
  doc.setTextColor(GOLD_DARK); doc.setFont("courier", "bold"); doc.setFontSize(11);
  doc.text(url.replace(/^https?:\/\//, ""), Wp / 2, y, { align: "center" });
  y += 16; doc.setTextColor("#52525b"); doc.setFont("helvetica", "normal"); doc.setFontSize(9);
  doc.text(t("price_list.footer"), Wp / 2, y, { align: "center" });
  doc.save(`${code}-price-list.pdf`);
}

const buildText = ({ storeName, tagline, groups, url, currency, t }) => {
  const ask = t("price_list.ask");
  const parts = [`${t("price_list.heading")} — ${storeName}`];
  const tg = (tagline || "").trim();
  if (tg) parts.push(tg);
  parts.push(t("price_list.prices_in", { currency }), "");
  for (const [cat, items] of groups) {
    parts.push(`— ${t(`categories.${cat}`, cat).toUpperCase()} —`);
    for (const p of items) {
      parts.push(`• ${p.name} — ${priceText(p, ask)}`);
      const d = brief(p.description);
      if (d) parts.push(`   ${d}`);
    }
    parts.push("");
  }
  parts.push(`${t("price_list.order_at")} ${url}`);
  return parts.join("\n");
};

/** v414.59 — shareable live price list (image + PDF + plain text) with the
 * distributor's store link attached. Always built fresh from the PUBLIC
 * store endpoint, so edited prices and store discounts are already applied. */
export function PriceListDialog({ code, storeName, onClose }) {
  const { t } = useTranslation();
  const qrRef = useRef(null);
  const [store, setStore] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tagline, setTagline] = useState(t("code_card.tagline_default"));
  const storeUrl = `${window.location.origin}/${code}`;

  useEffect(() => {
    api.get(`/store/${code}`)
      .then(({ data }) => setStore(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, [code]);

  const groups = useMemo(() => groupProducts(store?.products), [store]);
  const ctx = () => ({
    storeName, tagline, groups, url: storeUrl,
    currency: store?.currency || "CAD", code, t,
  });

  const shareImage = async () => {
    setBusy(true);
    try {
      const png = composePriceListPng(qrRef.current?.querySelector("canvas"), ctx());
      const how = await saveCardPng(png, `${code}-price-list.png`, storeUrl);
      if (how === "downloaded") toast.success(t("code_card.downloaded"));
      if (how === "shared") toast.success(t("code_card.share_saved"));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = () => {
    buildPdf(qrRef.current?.querySelector("canvas"), ctx());
    toast.success(t("price_list.pdf_saved"));
  };

  const copyText = () =>
    copyToClipboard(buildText(ctx()), t("price_list.text_copied"));

  const count = store?.products?.length || 0;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-zinc-200 rounded-xl shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        data-testid="price-list-dialog"
      >
        <h2 className="text-xl font-semibold tracking-tight">{t("price_list.title")}</h2>
        <p className="text-sm text-zinc-600 mt-1">{t("price_list.subtitle", { count })}</p>

        <div ref={qrRef} className="hidden" aria-hidden="true">
          <QRCodeCanvas value={storeUrl} size={480} fgColor="#09090b" level="M" includeMargin />
        </div>

        <div className="mt-3">
          <label className="block text-xs font-semibold mb-1 text-zinc-700">{t("code_card.tagline_label")}</label>
          <input
            type="text"
            value={tagline}
            maxLength={60}
            onChange={(e) => setTagline(e.target.value)}
            className="w-full h-9 px-3 text-sm border border-zinc-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c9a24a]/50"
            data-testid="price-list-tagline-input"
          />
        </div>

        {/* live preview of what goes on the list */}
        <div className="mt-3 border border-zinc-200 rounded-lg max-h-56 overflow-y-auto divide-y divide-zinc-100" data-testid="price-list-preview">
          {!store ? (
            <p className="p-3 text-sm text-zinc-500">{t("common.loading")}</p>
          ) : groups.length === 0 ? (
            <p className="p-3 text-sm text-zinc-500">{t("price_list.empty")}</p>
          ) : (
            groups.map(([cat, items]) => (
              <div key={cat} className="p-2.5">
                <p className="text-[10px] font-mono font-bold uppercase tracking-wider text-[#8a6a1c] mb-1">
                  {t(`categories.${cat}`, cat)}
                </p>
                {items.map((p) => (
                  <div key={p.name} className="flex justify-between gap-2 py-0.5">
                    <span className="text-xs text-zinc-800 truncate">{p.name}</span>
                    <span className="text-xs font-semibold shrink-0">{priceText(p, t("price_list.ask"))}</span>
                  </div>
                ))}
              </div>
            ))
          )}
        </div>

        <p className="text-[11px] text-zinc-500 mt-2" data-testid="price-list-live-note">{t("price_list.live_note")}</p>

        <div className="grid grid-cols-3 gap-2 mt-4">
          <BrutalButton size="sm" onClick={shareImage} disabled={busy || !store || count === 0} data-testid="price-list-share-image-btn">
            <ShareNetwork size={14} weight="bold" /> {t("price_list.share_image")}
          </BrutalButton>
          <BrutalButton size="sm" variant="secondary" onClick={downloadPdf} disabled={!store || count === 0} data-testid="price-list-pdf-btn">
            <FilePdf size={14} weight="bold" /> PDF
          </BrutalButton>
          <BrutalButton size="sm" variant="secondary" onClick={copyText} disabled={!store || count === 0} data-testid="price-list-copy-text-btn">
            <Copy size={14} weight="bold" /> {t("price_list.copy_text")}
          </BrutalButton>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="w-full mt-3 text-sm text-zinc-500 hover:text-zinc-800 transition-colors"
          data-testid="price-list-close-btn"
        >
          {t("common.close")}
        </button>
      </div>
    </div>
  );
}
